import { useEffect, useMemo, useState } from 'react';
import { triggerNotification } from '../notificationHelper';
import {
  getCartProductError,
  uploadProductAnswerMedia
} from './webstoreCheckoutUtilities';
import {
  getEcomStoreProductSelectOptions,
  getSelectedEcomStoreProductVariant,
  isCartVariantUnavailable
} from './webstoreHelper';
import { matchInputAnswers } from './useCartStorageController';
import imageNotAvailablePlaceholder from '../../images/image-not-available.png';
import { useRegCartController } from './useRegCartController';
import { PAYMENT_PROVIDER_LINE_ITEM_TYPES } from '../../config/constants';
import { webstoreTrackingHelper } from './webstoreTrackingHelper';

const useCartController = (
  identifier,
  {
    user,
    isAdminCheckout = false,
    imageUploadIdentifier = null,
    onAddProductsToCart,
    onUpdateCartProducts,
    onRedeemCartProductCoupon,
    onRemoveProductsFromCart,
    onFetchUserCart,
    onFetchCheckoutSessionResult,
    onFetchCheckoutInvoiceResult,
    onCheckoutUserCart,
    onMockUserCartCheckout,
    onShowContinueAsGuestModal,
    onShowProductCustomization,
    onToggleWebstoreCheckoutSummaryDrawer,
    cartShippingController,
    cartStorageController,
    userCart
  } = {}
) => {
  const {
    getGuestCartStorage,
    setCartSecureId,
    setCartFundraiser,
    resetCart,
    addGuestCartProductsAndPackages,
    updateGuestCartProductsAndPackages,
    onFetchGuestCartCallback
  } = cartStorageController || {};
  const { onShippingCallback } = cartShippingController || {};
  const imageUploadId = `${imageUploadIdentifier || 'user-cart'}-${identifier}`;

  const addCartProducts = (
    {
      pkEcomStore = null,
      regCartProducts = [],
      ecomStoreCartProducts = [],
      ecomStoreCartPackages = [],
      includeShipping = false,
      removeUnavailable = false,
      clearCart = false
    },
    onSuccessCallback = null,
    onErrorCallback = null
  ) => {
    const cart = getGuestCartStorage(identifier);

    const cartPackagesWithIndexes = ecomStoreCartPackages
      .filter((p) => !!p.ecomStoreCartProducts)
      .map((p, index) => ({
        ...p,
        ecomStoreCartProducts: p.ecomStoreCartProducts.map((e, index2) => ({
          ...e,
          searchIndex: `${index}-${index2}`
        }))
      }));

    const products = [
      ...ecomStoreCartProducts,
      ...cartPackagesWithIndexes.flatMap((p) => p.ecomStoreCartProducts)
    ];
    const cartProductErrors = products
      .map((c) => getCartProductError(c))
      .filter((e) => !!e);

    if (cartProductErrors.length > 0) {
      onErrorCallback(cartProductErrors[0], 'input');
    }
    else {
      uploadProductAnswerMedia(
        imageUploadId,
        products,
        (requestedStoreProducts) => {
          const requestData = {
            pkEcomStore,
            clearCart,
            regCartProducts,
            ecomStoreCartProducts: requestedStoreProducts.filter(
              (p) => !p.fkEcomStoreProductPackage
            ),
            ecomStoreCartPackages: cartPackagesWithIndexes.map((p) => ({
              ...p,
              ecomStoreCartProducts: requestedStoreProducts.filter(
                (rp) =>
                  rp.fkEcomStoreProductPackage ===
                    p.fkEcomStoreProductPackage &&
                  p.ecomStoreCartProducts.some(
                    (c) => c.searchIndex === rp.searchIndex
                  )
              )
            })),
            removeUnavailable,
            includeShipping,
            ...(includeShipping
              ? { ecomCustomerAddress: cart.shippingAddress }
              : {})
          };

          if (!isAdminCheckout && !!user?.pkUser) {
            onAddProductsToCart(
              identifier,
              requestData,
              (d) => {
                webstoreTrackingHelper.addStoreProductToCart(
                  d,
                  requestData,
                  d.regAssociation?.ecomStore
                );

                if (onSuccessCallback) {
                  onSuccessCallback(d);
                }
                onShippingCallback(d.cart.shippingCosts);
              },
              onErrorCallback
            );
          }
          else if (
            !isAdminCheckout &&
            cart.ecomStoreCartProducts.length === 0 &&
            onShowContinueAsGuestModal
          ) {
            onShowContinueAsGuestModal();
          }
          else {
            const {
              ecomStoreCartProducts: updatedCartProducts,
              ecomStoreCartPackages: updatedCartProductPackages
            } = addGuestCartProductsAndPackages(cart, requestData);

            onFetchUserCart(
              identifier,
              {
                regCartProducts,
                ecomStoreCartProducts: [...updatedCartProducts],
                ecomStoreCartPackages: [...updatedCartProductPackages],
                removeUnavailable,
                includeShipping
              },
              (data) => {
                if (!isAdminCheckout) {
                  webstoreTrackingHelper.addStoreProductToCart(
                    data,
                    requestData,
                    data.regAssociation?.ecomStore
                  );
                }

                onFetchGuestCartCallback(identifier, data, {
                  ecomStoreCartProducts: updatedCartProducts,
                  ecomStoreCartPackages: updatedCartProductPackages
                });
                if (onToggleWebstoreCheckoutSummaryDrawer) {
                  onToggleWebstoreCheckoutSummaryDrawer({ isOpen: true });
                }

                if (onSuccessCallback) {
                  onSuccessCallback(data);
                }
              },
              (e) => onErrorCallback(e)
            );
          }
        },
        onErrorCallback
      );
    }
  };

  const updateCartProducts = (
    {
      regCartProducts = [],
      ecomStoreCartProducts = [],
      ecomStoreCartPackages = [],
      includeShipping = false,
      removeUnavailable = false
    },
    onSuccessCallback = null,
    onErrorCallback = null
  ) => {
    const cart = getGuestCartStorage(identifier);

    const cartPackagesWithIndexes = ecomStoreCartPackages.map((p, index) => ({
      ...p,
      ecomStoreCartProducts:
        p.ecomStoreCartProducts?.map((e, index2) => ({
          ...e,
          searchIndex: `${index}-${index2}`
        })) || []
    }));

    const products = [
      ...ecomStoreCartProducts,
      ...cartPackagesWithIndexes.flatMap((p) => p.ecomStoreCartProducts)
    ];
    const cartProductErrors = products
      .map((c) => getCartProductError(c))
      .filter((e) => !!e);

    if (cartProductErrors.length > 0) {
      onErrorCallback(cartProductErrors[0], 'input');
    }
    else {
      uploadProductAnswerMedia(
        imageUploadId,
        products,
        (requestedStoreProducts) => {
          const requestData = {
            skipLoading: true,
            regCartProducts,
            ecomStoreCartProducts: requestedStoreProducts.filter(
              (p) => !p.fkEcomStoreProductPackage
            ),
            ecomStoreCartPackages: cartPackagesWithIndexes.map((p) => ({
              ...p,
              ecomStoreCartProducts: requestedStoreProducts.filter(
                (rp) =>
                  rp.fkEcomStoreProductPackage ===
                    p.fkEcomStoreProductPackage &&
                  p.ecomStoreCartProducts.some(
                    (c) => c.searchIndex === rp.searchIndex
                  )
              )
            })),
            removeUnavailable,
            includeShipping,
            ...(includeShipping
              ? { ecomCustomerAddress: cart.shippingAddress }
              : {})
          };

          if (!isAdminCheckout && !!user?.pkUser) {
            onUpdateCartProducts(
              identifier,
              requestData,
              (d) => {
                webstoreTrackingHelper.customizeStoreCartProducts(
                  d,
                  requestData,
                  d.regAssociation?.ecomStore
                );

                onShippingCallback(d.cart.shippingCosts);
                if (onSuccessCallback) {
                  onSuccessCallback(d);
                }
              },
              onErrorCallback
            );
          }
          else {
            const {
              ecomStoreCartProducts: updatedGuestProducts,
              ecomStoreCartPackages: updatedGuestPackages
            } = updateGuestCartProductsAndPackages(cart, requestData);

            onFetchUserCart(
              identifier,
              {
                ...requestData,
                ecomStoreCartProducts: [...updatedGuestProducts],
                ecomStoreCartPackages: [...updatedGuestPackages]
              },
              (data) => {
                if (!isAdminCheckout) {
                  webstoreTrackingHelper.customizeStoreCartProducts(
                    data,
                    requestData,
                    data.regAssociation?.ecomStore
                  );
                }

                onFetchGuestCartCallback(identifier, data, {
                  ecomStoreCartProducts: updatedGuestProducts,
                  ecomStoreCartPackages: updatedGuestPackages
                });
                if (onSuccessCallback) {
                  onSuccessCallback(data);
                }
              },
              onErrorCallback
            );
          }
        },
        onErrorCallback
      );
    }
  };

  const removeCartProducts = (
    {
      pkRegCartProducts = [],
      pkEcomStoreCartProducts = [],
      pkEcomStoreCartPackages = [],
      includeShipping = false
    },
    onSuccessCallback,
    onErrorCallback
  ) => {
    const cart = getGuestCartStorage(identifier);

    if (!isAdminCheckout && !!user?.pkUser) {
      onRemoveProductsFromCart(
        identifier,
        {
          pkEcomStoreCartProducts,
          pkEcomStoreCartPackages,
          pkRegCartProducts,
          ...(includeShipping
            ? { ecomCustomerAddress: cart.shippingAddress }
            : {})
        },
        (d) => {
          onShippingCallback(d.cart.shippingCosts);
          if (onSuccessCallback) {
            onSuccessCallback(d);
          }
        },
        onErrorCallback
      );

      return;
    }

    // guest cart
    const products = cart.ecomStoreCartProducts.filter(
      (f) =>
        !pkEcomStoreCartProducts.some((p) => p === f.pkEcomStoreCartProduct)
    );
    const packages = cart.ecomStoreCartPackages.filter(
      (f) =>
        !pkEcomStoreCartPackages.some((p) => p === f.pkEcomStoreCartPackage)
    );
    onFetchUserCart(
      identifier,
      {
        skipLoading: true,
        ecomStoreCartProducts: [...products],
        ecomStoreCartPackages: [...packages],
        includeShipping
      },
      (data) => {
        onFetchGuestCartCallback(identifier, data, {
          ecomStoreCartProducts: products,
          ecomStoreCartPackages: packages
        });

        if (onSuccessCallback) {
          onSuccessCallback(data);
        }
      },
      onErrorCallback
    );
  };

  const removeUnavailableCartProducts = (
    { includeShipping = false },
    onSuccessCallback,
    onErrorCallback
  ) => {
    updateCartProducts(
      { includeShipping, removeUnavailable: true },
      onSuccessCallback,
      onErrorCallback
    );
  };

  const showCartProductCustomization = ({
    ecomStoreCartProduct,
    ecomStoreCartPackage
  }) => {
    if (onShowProductCustomization) {
      onShowProductCustomization({
        ecomStoreCartProduct,
        ecomStoreCartPackage
      });
    }
  };

  const checkoutCart = (
    {
      email,
      paymentType,
      paymentPlanId,
      shippingOptions,
      fkEcomStoreRosterPlayer,
      fundraiserCustomInput,
      redirectPath
    },
    onSuccessCallback,
    onErrorCallback
  ) => {
    const cart = getGuestCartStorage(identifier);
    onCheckoutUserCart(
      identifier,
      {
        email,
        shippingOptions,
        paymentType,
        paymentPlanId,
        ecomCustomerAddress: cart.shippingAddress,
        ecomStoreCartPackages: cart.ecomStoreCartPackages,
        ecomStoreCartProducts: cart.ecomStoreCartProducts,
        fkEcomStoreRosterPlayer,
        fundraiserCustomInput,
        redirectPath
      },
      (data) => {
        webstoreTrackingHelper.initiateCheckout(
          userCart,
          data,
          userCart.regAssociation?.ecomStore,
          true
        );
        if (data.secureId) {
          setCartSecureId(identifier, data.secureId);
        }

        if (onSuccessCallback) {
          onSuccessCallback(data);
        }
      },
      onErrorCallback
    );
  };

  const mockStoreCartCheckout = (
    { fkEcomStoreRosterPlayer, fundraiserCustomInput },
    onSuccessCallback,
    onErrorCallback
  ) => {
    onMockUserCartCheckout(
      identifier,
      { fkEcomStoreRosterPlayer, fundraiserCustomInput },
      onSuccessCallback,
      onErrorCallback
    );
  };

  const clearCartBySecureId = (secureId) => {
    const cart = getGuestCartStorage(identifier);
    if (cart.secureId === secureId) {
      resetCart(identifier);
      onFetchUserCart(identifier);
    }
  };

  const setFundraiser = ({ fkEcomStoreRosterPlayer, customInput }) => {
    setCartFundraiser(identifier, { fkEcomStoreRosterPlayer, customInput });
  };

  const redeemCartProductCoupon = (
    data,
    onSuccessCallback,
    onErrorCallback
  ) => {
    const cart = getGuestCartStorage(identifier);

    onRedeemCartProductCoupon(
      identifier,
      {
        ...data,
        ...(data.includeShipping
          ? { ecomCustomerAddress: cart.shippingAddress }
          : {})
      },
      (d) => {
        onShippingCallback(d.cart.shippingCosts);
        if (onSuccessCallback) {
          onSuccessCallback(d);
        }
      },
      onErrorCallback
    );
  };

  const fetchUserCart = (data, onSuccessCallback, onErrorCallback) => {
    onFetchUserCart(identifier, data, onSuccessCallback, onErrorCallback);
  };

  const fetchCheckoutSessionResult = (
    { secureId, skipCheckoutComplete = false },
    onSuccessCallback,
    onErrorCallback
  ) => {
    onFetchCheckoutSessionResult(
      identifier,
      { secureId, skipCheckoutComplete },
      onSuccessCallback,
      onErrorCallback
    );
  };

  const fetchCheckoutInvoiceResult = (
    { invoiceId, secureId, skipCheckoutComplete = false },
    onSuccessCallback,
    onErrorCallback
  ) => {
    onFetchCheckoutInvoiceResult(
      identifier,
      { invoiceId, secureId, skipCheckoutComplete },
      onSuccessCallback,
      onErrorCallback
    );
  };

  return {
    isGuest: !user?.pkUser,
    user,
    cartShippingController,
    cartStorageController,
    fetchUserCart,
    addCartProducts,
    updateCartProducts,
    redeemCartProductCoupon,
    removeCartProducts,
    removeUnavailableCartProducts,
    checkoutCart,
    mockStoreCartCheckout,
    setFundraiser,
    clearCartBySecureId,
    fetchCheckoutSessionResult,
    fetchCheckoutInvoiceResult,
    showCartProductCustomization
  };
};

const getBundleLineItemInfo = (
  ecomStoreCart,
  ecomStoreCartPackage,
  lineItem
) => {
  const cartProducts = ecomStoreCart?.ecomStoreCartProducts ?? [];
  const vendorBundleProducts =
    ecomStoreCartPackage?.ecomStoreProductPackage?.ecomVendorBundle
      ?.ecomVendorBundleProducts ?? [];

  const cartProductMap = new Map(
    cartProducts.map((p) => [p.pkEcomStoreCartProduct, p])
  );
  const vendorBundleProductMap = new Map(
    vendorBundleProducts.map((vbp) => [vbp.fkEcomVendorProduct, vbp])
  );

  const grouped = new Map();
  (lineItem?.bundleItems ?? []).forEach((bi) => {
    const cartProduct = cartProductMap.get(bi.entityId);
    if (!cartProduct) return;

    const latestVariant = ecomStoreCart.latestVersionEcomStoreCartProducts?.find(
      (p) =>
        p.ecomVendorProductVariant.uuid ===
        cartProduct.ecomStoreProductVariant.ecomVendorProductVariant.uuid
    );
    const isUnavailable =
      !!latestVariant && isCartVariantUnavailable(latestVariant);

    const vendorProduct =
      cartProduct.ecomStoreProductVariant.ecomStoreProduct.ecomVendorProduct;
    const ecomVendorBundleProduct = vendorBundleProductMap.get(
      vendorProduct.pkEcomVendorProduct
    );

    const bundleItemHasVariantImages =
      vendorProduct?.hasVariantAssignedImages ?? false;

    const bundleItemImage = bundleItemHasVariantImages
      ? bi.variantImage || imageNotAvailablePlaceholder
      : bi.previewImage;

    const quantityLeft =
      cartProduct.ecomStoreProductVariant.ecomVendorProductVariant.quantity;
    const outOfStock = quantityLeft != null && quantityLeft <= 0;
    const tooManyInCart =
      quantityLeft != null && quantityLeft - bi.quantity < 0;

    if (!grouped.has(vendorProduct.pkEcomVendorProduct)) {
      grouped.set(vendorProduct.pkEcomVendorProduct, {
        items: [],
        ecomStoreCartProduct: cartProduct,
        ecomVendorBundleProduct,
        totalQuantity: 0,
        sort: ecomVendorBundleProduct?.sort ?? 0
      });
    }

    const group = grouped.get(vendorProduct.pkEcomVendorProduct);
    group.totalQuantity += bi.quantity;
    group.items.push({
      lineItem: bi,
      cartProduct,
      quantityLeft,
      outOfStock,
      tooManyInCart,
      isUnavailable,
      latestVariant,
      previewImage: bundleItemImage,
      sortName: bi.addOns[0]?.value ?? '',
      secondSortName: bi.addOns[1]?.value ?? ''
    });
  });

  return [...grouped.values()]
    .map((v) => {
      const itemPerBundleCount = v.items.reduce(
        (r, c) => r + c.lineItem.bundleQuantity,
        0
      );

      return {
        ...v,
        itemPerBundleCount,
        tooManySelected:
          v.ecomVendorBundleProduct &&
          itemPerBundleCount > v.ecomVendorBundleProduct.count,
        notEnoughSelected:
          v.ecomVendorBundleProduct &&
          itemPerBundleCount < v.ecomVendorBundleProduct.count,
        items: v.items.sort(
          (a, b) =>
            a.sortName.localeCompare(b.sortName) ||
            a.secondSortName.localeCompare(b.secondSortName)
        )
      };
    })
    .sort((a, b) => a.sort - b.sort);
};

const useCartLineItemDetails = (ecomStoreCart) => {
  const lineItemInfo = useMemo(
    () =>
      ecomStoreCart?.checkoutCart.lineItems
        .filter(
          (i) =>
            i.lineItemType === PAYMENT_PROVIDER_LINE_ITEM_TYPES.ecommerce.value
        )
        .map((lineItem) => {
          const ecomStoreCartProduct = ecomStoreCart.ecomStoreCartProducts.find(
            (p) => p.pkEcomStoreCartProduct === lineItem.entityId
          );
          const ecomStoreCartPackage = ecomStoreCart.ecomStoreCartPackages?.find(
            (p) => p.pkEcomStoreCartPackage === lineItem.entityId
          );
          const latestVersionEcomStoreCartProduct = ecomStoreCart.latestVersionEcomStoreCartProducts?.find(
            (p) =>
              p.ecomVendorProductVariant.uuid ===
              ecomStoreCartProduct?.ecomStoreProductVariant
                .ecomVendorProductVariant.uuid
          );

          const hasVariantAssignedImages =
            ecomStoreCartProduct?.ecomStoreProductVariant.ecomStoreProduct
              .ecomVendorProduct.hasVariantAssignedImages ?? false;
          const previewImage = hasVariantAssignedImages
            ? lineItem.variantImage || imageNotAvailablePlaceholder
            : lineItem.previewImage;

          const packageItemsWithReferences = getBundleLineItemInfo(
            ecomStoreCart,
            ecomStoreCartPackage,
            lineItem
          );

          const quantityLeft = ecomStoreCartProduct
            ? ecomStoreCartProduct.ecomStoreProductVariant
                .ecomVendorProductVariant.quantity
            : null;
          const outOfStock = quantityLeft != null && quantityLeft <= 0;
          const tooManyInCart =
            quantityLeft != null && quantityLeft - lineItem.quantity < 0;

          const hasChanges =
            !!latestVersionEcomStoreCartProduct ||
            (ecomStoreCartPackage &&
              (ecomStoreCartPackage.hasChanges ||
                packageItemsWithReferences.some((pr) =>
                  pr.items.some((i) => !!i.latestVariant)
                )));

          const requiresBundleUpdate =
            (ecomStoreCartPackage &&
              ecomStoreCartPackage.missingPkEcomVendorProducts.length > 0) ||
            packageItemsWithReferences.some(
              (p) =>
                p.tooManySelected ||
                p.notEnoughSelected ||
                p.items.some((i) => i.tooManyInCart || i.outOfStock)
            );

          const isUnavailable =
            (ecomStoreCartPackage &&
              (ecomStoreCartPackage.isUnavailable ||
                packageItemsWithReferences.some((pr) =>
                  pr.items.some((i) => i.isUnavailable)
                ))) ||
            (latestVersionEcomStoreCartProduct &&
              isCartVariantUnavailable(latestVersionEcomStoreCartProduct));

          return {
            entityId: lineItem.entityId,
            previewImage,
            hasVariantAssignedImages,
            quantityLeft,
            outOfStock,
            tooManyInCart,
            isUnavailable,
            hasChanges,
            requiresBundleUpdate,
            latestVersionEcomStoreCartProduct,
            packageItemsWithReferences,
            ecomStoreCartProduct,
            ecomStoreCartPackage,
            urlHandle: ecomStoreCartProduct
              ? ecomStoreCartProduct.ecomStoreProductVariant.ecomStoreProduct
                  .urlHandle
              : ecomStoreCartPackage.urlHandle
          };
        }) || [],
    [ecomStoreCart]
  );

  return { lineItems: lineItemInfo };
};

const useCartProductVariantSelector = (
  ecomStoreProduct,
  { ecomStoreCartPackage, ecomStoreCartProduct, isCheckoutView, analytics }
) => {
  const [formState, setFormState] = useState({
    initialized: false,
    ecomStoreCartProducts: [],
    adjustments: {},
    optionErrors: [],
    count: 1,
    loading: false
  });
  const {
    ecomStoreCart,
    addCartProducts,
    updateCartProducts
  } = useRegCartController();
  const selectOptionInfo = getEcomStoreProductSelectOptions(
    ecomStoreProduct,
    formState.ecomStoreCartProducts,
    ecomStoreCartProduct
  );
  const {
    storeProducts,
    storeProductOptions,
    selectedVariants,
    isBundleProduct
  } = selectOptionInfo;

  useEffect(() => {
    if (ecomStoreProduct) {
      const cartProducts = ecomStoreCartPackage
        ? ecomStoreCart.ecomStoreCartProducts.filter(
            (p) =>
              p.fkEcomStoreCartPackage ===
              ecomStoreCartPackage.pkEcomStoreCartPackage
          )
        : ecomStoreCartProduct
        ? [ecomStoreCartProduct]
        : null;
      const { productStates, adjustments } = cartProducts
        ? // eslint-disable-next-line no-use-before-define
          getInitialPackageProductFormStateData(cartProducts)
        : {};

      setFormState({
        initialized: true,
        ecomStoreCartProducts: productStates
          ? [
              ...productStates,
              ...storeProductOptions
                .filter(
                  (p) =>
                    !productStates.some(
                      (ps) => ps.pkEcomStoreProduct === p.pkEcomStoreProduct
                    )
                )
                .map((p) => {
                  adjustments[p.defaultSelectState.fkEcomVendorProduct] = {
                    change: p.defaultSelectState.count,
                    type: 'new'
                  };

                  return p.defaultSelectState;
                })
            ]
          : storeProductOptions.map((p) => p.defaultSelectState),
        adjustments: adjustments || {},
        optionErrors: [],
        count: ecomStoreCartPackage
          ? ecomStoreCartPackage.count
          : ecomStoreCartProduct?.count || 1,
        loading: false
      });
    }
  }, [ecomStoreProduct, ecomStoreCartProduct, ecomStoreCartPackage]);

  const getInitialPackageProductFormStateData = (cartProducts) => {
    const cartPackage = ecomStoreCartPackage;
    const cartPackageCount = cartPackage?.count || 1;

    const bundleProductsMap = new Map(
      cartPackage?.ecomStoreProductPackage?.ecomVendorBundle?.ecomVendorBundleProducts?.map(
        (bp) => [bp.fkEcomVendorProduct, bp.count]
      ) || []
    );

    const adjustments = {};

    const processedProducts = cartProducts.reduce((acc, p) => {
      const productVariant = p.ecomStoreProductVariant;
      const storeProduct = productVariant.ecomStoreProduct;
      const vendorId = storeProduct.ecomVendorProduct.pkEcomVendorProduct;

      const current = {
        pkEcomStoreCartProduct: p.pkEcomStoreCartProduct,
        pkEcomStoreProduct: storeProduct.pkEcomStoreProduct,
        fkEcomVendorProduct: vendorId,
        count: p.count / cartPackageCount,
        fkEcomStoreCartPackage: cartPackage?.pkEcomStoreCartPackage,
        fkEcomStoreProductPackage:
          cartPackage?.ecomStoreProductPackage?.pkEcomStoreProductPackage,
        selectedOptions: productVariant.ecomVendorProductVariant.ecomVendorProductVariantOptions.map(
          ({ nameSort, name, value }) => ({ nameSort, name, value })
        ),
        ecomStoreCartProductInputAnswers: p.ecomStoreCartProductInputAnswers.map(
          (a) => ({
            ...a,
            isAutopopulated: ecomStoreProduct.ecomStoreProductInputAnswers.some(
              (is) =>
                is.fkRegFormControlType === a.fkRegFormControlType &&
                (is.fkEcomVendorProductInput === a.fkEcomVendorProductInput ||
                  is.label.toLowerCase() === a.label.toLowerCase())
            )
          })
        )
      };

      const bundleMaxCount = bundleProductsMap.get(vendorId);

      if (!acc.has(vendorId)) {
        acc.set(vendorId, {
          totalCount: 0,
          originalTotal: 0,
          states: []
        });
      }

      const group = acc.get(vendorId);

      group.originalTotal += current.count;

      if (bundleMaxCount !== undefined) {
        const remainingCapacity = bundleMaxCount - group.totalCount;
        const allowedCount = Math.min(current.count, remainingCapacity);

        if (allowedCount > 0) {
          group.states.push({ ...current, count: allowedCount });
          group.totalCount += allowedCount;
        }
      }
      else {
        group.states.push(current);
        group.totalCount += current.count;
      }

      return acc;
    }, new Map());

    // Post-process to fill missing bundle quantities
    for (const [vendorId, group] of processedProducts.entries()) {
      const bundleMaxCount = bundleProductsMap.get(vendorId);

      if (bundleMaxCount !== undefined) {
        if (group.totalCount < bundleMaxCount && group.states.length > 0) {
          const remaining = bundleMaxCount - group.totalCount;

          group.states[0].count += remaining;
          group.totalCount += remaining;
        }

        const delta = group.totalCount - group.originalTotal;

        if (delta !== 0) {
          adjustments[vendorId] = {
            change: delta,
            type: delta > 0 ? 'added' : 'removed'
          };
        }
      }
    }

    const productStates = [...processedProducts.values()].flatMap(
      (p) => p.states
    );

    return {
      productStates,
      adjustments
    };
  };

  const getRequestData = () =>
    isBundleProduct
      ? {
          pkEcomStore: ecomStoreProduct.fkEcomStore,
          includeShipping: isCheckoutView,
          ecomStoreCartPackages: [
            {
              pkEcomStoreCartPackage:
                ecomStoreCartPackage?.pkEcomStoreCartPackage,
              fkEcomStoreProductPackage:
                ecomStoreProduct.ecomStoreProductPackage
                  .pkEcomStoreProductPackage,
              count: formState.count,
              analytics: analytics
                ? { ctaCaptured: analytics.ctaCaptured }
                : null,
              ecomStoreCartProducts: formState.ecomStoreCartProducts
                .reduce((r, c) => {
                  const existing = r.find(
                    (f) =>
                      f.selectedOptions.every((op) =>
                        c.selectedOptions.find(
                          (sop) =>
                            sop.name === op.name && sop.value === op.value
                        )
                      ) &&
                      matchInputAnswers(
                        f.ecomStoreCartProductInputAnswers,
                        c.ecomStoreCartProductInputAnswers
                      )
                  );
                  if (existing) {
                    existing.count += c.count;
                  }
                  else {
                    const existingCartProduct = ecomStoreCartPackage
                      ? ecomStoreCart.ecomStoreCartProducts.find(
                          (p) =>
                            p.fkEcomStoreCartPackage ===
                              ecomStoreCartPackage.pkEcomStoreCartPackage &&
                            p.ecomStoreProductVariant.ecomVendorProductVariant.ecomVendorProductVariantOptions.every(
                              (op) =>
                                c.selectedOptions.find(
                                  (sop) =>
                                    sop.name === op.name &&
                                    sop.value === op.value
                                )
                            ) &&
                            matchInputAnswers(
                              p.ecomStoreCartProductInputAnswers,
                              c.ecomStoreCartProductInputAnswers
                            ) &&
                            !r.find(
                              (rf) =>
                                rf.fkEcomStoreCartProduct ===
                                p.pkEcomStoreCartProduct
                            )
                        )
                      : null;

                    r.push({
                      ...c,
                      pkEcomStoreCartProduct:
                        existingCartProduct?.pkEcomStoreCartProduct || null
                    });
                  }
                  return r;
                }, [])
                .map((p) => {
                  const {
                    product,
                    selectedVariant
                  } = getSelectedEcomStoreProductVariant(p, storeProducts);
                  return {
                    pkEcomStoreCartProduct: p.pkEcomStoreCartProduct,
                    uuid: selectedVariant?.ecomVendorProductVariant.uuid,
                    fkEcomStoreProductPackage:
                      ecomStoreProduct.ecomStoreProductPackage
                        ?.pkEcomStoreProductPackage,
                    count: p.count,
                    ecomStoreCartProductInputAnswers:
                      p.ecomStoreCartProductInputAnswers,
                    ecomStoreProduct: product
                  };
                })
                .filter((p) => !!p.uuid)
            }
          ]
        }
      : {
          pkEcomStore: ecomStoreProduct.fkEcomStore,
          includeShipping: isCheckoutView,
          ecomStoreCartProducts: [
            {
              pkEcomStoreCartProduct:
                ecomStoreCartProduct?.pkEcomStoreCartProduct,
              uuid: selectedVariants[0]?.ecomVendorProductVariant.uuid,
              count: formState.count,
              analytics: analytics
                ? { ctaCaptured: analytics.ctaCaptured }
                : null,
              ecomStoreCartProductInputAnswers:
                formState.ecomStoreCartProducts[0]
                  .ecomStoreCartProductInputAnswers,
              ecomStoreProduct
            }
          ]
        };

  const onAddProductToCart = (onAddOrUpdateCallback = null) => {
    const requestData = getRequestData();

    setFormState({ ...formState, optionErrors: [], loading: true });
    addCartProducts(
      requestData,
      (d) => {
        setFormState({
          ...formState,
          optionErrors: [],
          loading: false
        });

        if (onAddOrUpdateCallback) {
          onAddOrUpdateCallback(d);
        }
      },
      (error, type) => {
        if (type === 'input') {
          setFormState({
            ...formState,
            optionErrors: [error],
            loading: false
          });
        }
        else {
          triggerNotification(error);
          setFormState({
            ...formState,
            optionErrors: [],
            loading: false
          });
        }
      }
    );
  };

  const onUpdateCartProduct = (onAddOrUpdateCallback) => {
    const requestData = getRequestData();

    setFormState({ ...formState, optionErrors: [], loading: true });
    updateCartProducts(
      requestData,
      (d) => {
        setFormState({
          ...formState,
          optionErrors: [],
          loading: false
        });

        if (onAddOrUpdateCallback) {
          onAddOrUpdateCallback(d);
        }
      },
      (error, type) => {
        if (type === 'input') {
          setFormState({
            ...formState,
            optionErrors: [error],
            loading: false
          });
        }
        else {
          triggerNotification(error);
          setFormState({
            ...formState,
            optionErrors: [],
            loading: false
          });
        }
      }
    );
  };

  const onVariantChange = (pkEcomStoreProduct, options) => {
    setFormState({
      ...formState,
      ecomStoreCartProducts: formState.ecomStoreCartProducts.map((p) =>
        p.pkEcomStoreProduct === pkEcomStoreProduct
          ? {
              ...p,
              selectedOptions: options
            }
          : p
      ),
      optionErrors: []
    });
  };

  const onInputAnswerChange = (pkEcomStoreProduct, answers) =>
    setFormState({
      ...formState,
      ecomStoreCartProducts: formState.ecomStoreCartProducts.map((p) =>
        p.pkEcomStoreProduct === pkEcomStoreProduct
          ? {
              ...p,
              ecomStoreCartProductInputAnswers: answers
            }
          : p
      ),
      optionErrors: []
    });

  const onBundleProductVariantSelect = (pkEcomStoreProduct, optionStates) => {
    setFormState({
      ...formState,
      ecomStoreCartProducts: [
        ...formState.ecomStoreCartProducts.filter(
          (f) => f.pkEcomStoreProduct !== pkEcomStoreProduct
        ),
        ...optionStates
      ],
      optionErrors: []
    });
  };

  const onQuantityChange = (count) => {
    setFormState({
      ...formState,
      optionErrors: [],
      count
    });
  };

  return {
    formState,
    selectOptionInfo,
    onAddProductToCart,
    onUpdateCartProduct,
    onVariantChange,
    onInputAnswerChange,
    onBundleProductVariantSelect,
    onQuantityChange
  };
};

export {
  useCartController,
  useCartLineItemDetails,
  useCartProductVariantSelector
};
